import { ArrowRight } from "lucide-react";

const steps = [
  {
    n: "01",
    title: "One-on-One Initial Consultation",
    desc: "We sit down to understand what you're really looking for — and what you're not. No pressure, no script — just clarity."
  },
  {
    n: "02",
    title: "The Brief — Buyer & Seller Psychology",
    desc: "Locations, non-negotiables, strategy: forever home or stepping stone? Add value or buy turnkey? We define the brief together — and unpack how sellers and agents think on the other side."
  },
  {
    n: "03",
    title: "Get Your Ducks in a Row",
    desc: "Brokers, banks, accountants, timeframes, conditions — all aligned before we ever step into an open home. This is what lets us act faster than the competition."
  },
  {
    n: "04",
    title: "Source the Right Properties",
    desc: "Pre-market, off-market and on-market opportunities — sourced through industry relationships you can't access alone. You see what most buyers never will."
  },
  {
    n: "05",
    title: "Inspecting With Purpose",
    desc: "What to look for. What to say to agents. What absolutely not to say. Every inspection becomes an intelligence-gathering exercise — not a wishful walk-through."
  },
  {
    n: "06",
    title: "Market & Property Analysis",
    desc: "Comparable sales, value indicators and preliminary due diligence — checking overlays, flood, bushfire and other hazards before emotion takes over."
  },
  {
    n: "07",
    title: "Offer, Negotiate, Win",
    desc: "Eliminate competition. Don't overpay. Act with confidence. This is where most retail buyers either freeze or get fleeced — it's also where I add the most value."
  },
  {
    n: "08",
    title: "Control the Asset",
    desc: "Property under contract, locked away. Now we finalise due diligence, finance and approvals — without the panic that comes from doing it the wrong order."
  },
  {
    n: "09",
    title: "Settlement Prep",
    desc: "Coordinated handover so settlement day is a non-event — pre-settlement inspection, utilities, insurance, keys. You move in, not around it."
  }
];

export default function HomeBuyerPackage() {
  return (
    <section
      id="home-buyers"
      data-testid="home-buyer-package-section"
      className="border-t border-neutral-800"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-7">
            <p className="overline mb-4">Cairns Home Buyers · The Success Package</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              From confusion to clarity. <br />
              <span className="italic text-[#36C281]">Buy like a professional.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="text-neutral-400 leading-relaxed mb-5">
              A complete, step-by-step process for home buyers — designed to help you beat the competition, not overpay, and avoid the costly mistakes that catch out 90% of first- and second-time buyers.
            </p>
            <a
              href="#contact"
              data-testid="home-buyer-cta-btn"
              className="btn-primary"
            >
              Start Your Journey <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* 9 steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-neutral-800">
          {steps.map((s, i) => {
            const isLastRow3 = i >= 6;        // lg: bottom row gets no bottom border
            const isLastRow2 = i >= 7;        // md: last two cards
            const isLastInRow3 = (i + 1) % 3 === 0;
            return (
              <div
                key={s.n}
                data-testid={`home-buyer-step-${s.n}`}
                className={[
                  "p-8 group hover:bg-[#131C18] transition-colors duration-300 relative",
                  // bottom borders
                  isLastRow3 ? "lg:border-b-0" : "lg:border-b",
                  isLastRow2 ? "md:border-b-0" : "md:border-b",
                  "border-b lg:border-b",
                  "border-neutral-800",
                  // right borders
                  "md:border-r lg:border-r",
                  isLastInRow3 ? "lg:border-r-0" : "",
                  (i + 1) % 2 === 0 ? "md:border-r-0 lg:border-r" : ""
                ].join(" ")}
              >
                <div className="flex items-baseline gap-4 mb-5">
                  <span className="font-serif-display text-4xl text-[#36C281]/40 group-hover:text-[#36C281] transition-colors">
                    {s.n}
                  </span>
                  <h3 className="font-serif-display text-2xl leading-tight">{s.title}</h3>
                </div>
                <p className="text-neutral-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Closing line */}
        <div className="mt-16 border-l-2 border-[#36C281]/40 pl-8 max-w-3xl">
          <p className="font-serif-display text-2xl md:text-3xl italic text-neutral-200 leading-snug">
            You stop guessing. You stop overpaying. You start buying like the people who do this every day — because now, one of them is on your team.
          </p>
        </div>
      </div>
    </section>
  );
}
